import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { FormDropdown as Dropdown } from '@ami/react/core';
import ProductOptionSetComponent from './ProductOptionSetComponent';
import { QuoteProductOption, couplerDecision } from '../otherQuoteEntities';
import { ProductConfiguration } from '../../products/productConfiguration';
import { QuoteCouplerStyle } from '../../quotes/quoteProductConfiguration';
import _ from 'lodash';
import { OptionSetInfo } from './optionSetInfo';
import { makeConcealable } from '@ami/react/core';
import ProductNotes from './ProductNotes';
import { findDOMNode } from 'react-dom';
import { FormGroup } from 'reactstrap';
import { FormToggle,FormInput } from '@ami/react/core';
import { isAmiUserCanEditPaintColor } from '../../authorization/amiPermissions';

class ScrollView extends React.Component {
  static childContextTypes = {
    scroll: PropTypes.object,
  };
  elements = {};
  register = (name, ref) => {
    this.elements[name] = ref;
  };
  unregister = (name) => {
    delete this.elements[name];
  };
  getChildContext() {
    return {
      scroll: {
        register: this.register,
        unregister: this.unregister,
      },
    };
  }
  scrollTo = (name) => {
    const node = findDOMNode(this.elements[name]);
    if (node && window.innerWidth < 760) {
      window.scrollTo({
        top: node.offsetTop + node.offsetParent.offsetTop - window.innerWidth,
        behavior: 'smooth',
      });
    }
  };
  render() {
    return React.Children.only(this.props.children);
  }
}

class ScrollElement extends React.Component {
  static contextTypes = {
    scroll: PropTypes.object,
  };
  componentDidMount() {
    this.context.scroll.register(this.props.name, this._element);
  }
  componentWillUnmount() {
    this.context.scroll.unregister(this.props.name);
  }

  render() {
    return React.cloneElement(this.props.children, {
      ref: (ref) => (this._element = ref),
    });
  }
}


const Content = makeConcealable(
  ({
    optionSetInfoList,
    updateProductOptionSelection,
    getSelectedProductOptionIdFor,
    getDefaultSeriesIdFor,
    scrollTo,
    onLastOptionSelected,
    originalProductOptions,
    isInventoryMode,
    selectedInventoryItem,
    selectedProduct,
    productGroup,
    hideLeadTimeAndPrices,
  }) => {    
    return (
      <div>
        <div className="hr-divider">
          <h3 className="hr-divider-content hr-divider-heading">
            Select Options
          </h3>
        </div>
        {isInventoryMode && (
          <p>
            Note: Only options that correspond to the selected product's weight
            categories are available.
          </p>
        )}
     
        {optionSetInfoList
          .filter((x) => !x.hideFromUser)
          .map((setInfo, index) => {
            const optionSetId = setInfo.optionSet.id;
            const isSelectedInInventoryItem =
              selectedInventoryItem &&
              selectedInventoryItem.productOptions.some(
                (x) => x.productOptionSetId === optionSetId
              );
            const optionSetInventorySetting = isSelectedInInventoryItem
              ? setInfo.optionSet.inventorySetting
              : null;
            return (
              <ScrollElement name={index} key={optionSetId}>
                <ProductOptionSetComponent
                  key={optionSetId}
                  selectedProduct={selectedProduct}
                  productGroup={productGroup}
                  setInfo={setInfo}
                  index={index}
                  onProductOptionSelected={(
                    selectedProductionOptionId,
                    index
                  ) => {
                    updateProductOptionSelection(
                      optionSetId,
                      selectedProductionOptionId
                    );
                    if (selectedProductionOptionId) {
                      scrollTo(index + 1);
                      if (
                        index + 1 === optionSetInfoList.length &&
                        onLastOptionSelected
                      ) {
                        onLastOptionSelected();
                      }
                    }
                  }}
                  selectedProductOptionId={getSelectedProductOptionIdFor(
                    optionSetId
                  )}
                  defaultSeriesId={getDefaultSeriesIdFor(setInfo)}
                  originalProductOption={originalProductOptions.find(
                    (x) => x.productOptionSetId === optionSetId
                  )}
                  isInventoryMode={isInventoryMode}
                  optionSetInventorySetting={optionSetInventorySetting}
                  hideLeadTimeAndPrices={hideLeadTimeAndPrices}
                />
              </ScrollElement>
            );
          })}
          
      </div>
    );
  }
);

export class ProductOptionSetsContainer extends React.Component {
  constructor(props) {
    super(props);
    this.updateProductOptionSelection =
      this.updateProductOptionSelection.bind(this);
    this.getSelectedProductOptionIdFor =
      this.getSelectedProductOptionIdFor.bind(this);
    this.getDefaultSeriesIdFor = this.getDefaultSeriesIdFor.bind(this);
  }

  static propTypes = {
    productGroup: PropTypes.instanceOf(Object).isRequired,
    couplerDecision: PropTypes.string,
    couplerOilReady: PropTypes.string,
    couplerStyle: PropTypes.instanceOf(QuoteCouplerStyle),
    selectedProductOptions: PropTypes.arrayOf(
      PropTypes.instanceOf(QuoteProductOption)
    ).isRequired,
    onProductChange: PropTypes.func.isRequired,
    selectedProduct: PropTypes.instanceOf(Object),
    selectedInventoryItem: PropTypes.instanceOf(Object),
    productOptionSets: PropTypes.arrayOf(Object).isRequired,
    productConfiguration: PropTypes.instanceOf(ProductConfiguration).isRequired,
    preferredBrandId: PropTypes.string,
    preferredSeriesId: PropTypes.string,
    notes: PropTypes.string,
    manufacturingInstructions: PropTypes.string,
    paintColor: PropTypes.string,
    onLastOptionSelected: PropTypes.func,
    isEditProductMode: PropTypes.bool.isRequired,
    originalProductOptions: PropTypes.arrayOf(
      PropTypes.shape({
        productOptionSetId: PropTypes.string.isRequired,
        productOptionId: PropTypes.string.isRequired,
        step3Price: PropTypes.number.isRequired,
        step3Msrp: PropTypes.number.isRequired,
      })
    ),
    hideLeadTimeAndPrices: PropTypes.bool.isRequired,
  };

  static defaultProps = {
    originalProductOptions: [],
  };

  
  state = {
    allProductOptionSeries: [],
    optionSetInfoList: [],
    showInstructions :  false,
    showPaintColor :  false   
  };

  componentDidMount() {
    const allProductOptionSeries = _(
      this.props.productConfiguration.productOption.brands
    )
      .sortBy((x) => x.name)
      .value()
      .map((x) => x.series)
      .flat();
    this.setState({ allProductOptionSeries }, () => {
      this.loadOptionSetsInfo(this.props.isEditProductMode ? false : true);
    });
    this.setState({showInstructions : !!this.props.manufacturingInstructions})
    this.setState({showPaintColor : !!this.props.paintColor && !this.props.isInventoryMode})
    // in case it's pre selected from product mode
   if( this.props.isInventoryMode && !!this.props.paintColor )
      this.onPaintColorChange(undefined);
  }

  componentDidUpdate(prevProps) {
    const productGroupChanged =
      prevProps.productGroup.id !== this.props.productGroup.id;

    const selectedProductChanged = function () {
      const prevId = (prevProps.selectedProduct || {}).id;
      const currentId = (this.props.selectedProduct || {}).id;
      return prevId !== currentId;
    }.bind(this)();

    const selectedInventoryItemChanged = function () {
      const prevId = (prevProps.selectedInventoryItem || {}).id;
      const currentId = (this.props.selectedInventoryItem || {}).id;
      return prevId !== currentId;
    }.bind(this)();

    const selectedProductOptionsChanged = function () {
      const prevOptions =
        prevProps.selectedProductOptions
          .filter((o) => o.productOptionId && o.productOptionId !== null)
          .map((o) => `${o.productOptionSetId}-${o.productOptionId}`) || [];
      const currOptions =
        this.props.selectedProductOptions
          .filter((o) => o.productOptionId && o.productOptionId !== null)
          .map((o) => `${o.productOptionSetId}-${o.productOptionId}`) || [];
      const isEqual = _.isEqual(prevOptions.sort(), currOptions.sort());
      return !isEqual;
    }.bind(this)();

    if (
      productGroupChanged ||
      selectedProductChanged ||
      selectedInventoryItemChanged ||
      selectedProductOptionsChanged
    ) {
      this.loadOptionSetsInfo(
        productGroupChanged || selectedInventoryItemChanged
      );
    }
  }

  getQuantityForOptionSet = (optionSetId) => {
    if (!this.props.selectedProduct) return 0;
    const found = this.props.selectedProduct.optionSetQuantities.find(
      (x) => x.optionSetId === optionSetId
    );
    return found ? found.quantity : 0;
  };

  loadOptionSetsInfo = (initializeSelections) => {
    const optionSetInfoList = this.props.productOptionSets.map(
      (optionSet) =>
        new OptionSetInfo(
          optionSet,
          this.state.allProductOptionSeries,
          this.getQuantityForOptionSet(optionSet.id),
          this.props.preferredBrandId,
          this.props.preferredSeriesId,
          false,
          false
        )
    );

    // Set the availability of each product option based on its dependencies
    const defaultSelectedOptions = [];
    for (var i = 0; i < optionSetInfoList.length; i++) {
      const optionSetInfo = optionSetInfoList[i];
      const availableProductOptions = [];
      for (
        var j = 0;
        j < optionSetInfo.optionSet.productOptionsBySeries.length;
        j++
      ) {
        const productOptionBySeries =
          optionSetInfo.optionSet.productOptionsBySeries[j];

        for (var k = 0; k < productOptionBySeries.productOptions.length; k++) {
          const productOption = productOptionBySeries.productOptions[k];
          const productOptionDependency =
            this.props.productGroup.productOptionDependencies.find(
              (x) => x.productOptionId === productOption.id
            );
          productOption.available = this.isProductOptionDependencyMet(
            productOptionDependency
          );
          if (productOption.available) {
            availableProductOptions.push(productOption);
          }
        }
      }

      optionSetInfo.hasAvailableOptions = availableProductOptions.length > 0;

      if (optionSetInfo.optionSet.hideIfSingleOptionAvailable && availableProductOptions.length === 1) {
        optionSetInfo.hideFromUser = true;
        // Don't include this in the visible option set, just select the single available option
        defaultSelectedOptions.push(
          new QuoteProductOption({
            productOptionSetId: optionSetInfo.optionSet.id,
            productOptionId: availableProductOptions[0].id,
          })
        );
      } else {
        defaultSelectedOptions.push(
          new QuoteProductOption({
            productOptionSetId: optionSetInfo.optionSet.id,
            productOptionId: this.getDefaultProductOptionIdFor(optionSetInfo),
          })
        );
      }
    }

    this.setState({ optionSetInfoList: optionSetInfoList }, () => {
      if (initializeSelections) {
        this.props.onProductChange({productOptions: defaultSelectedOptions}, true);
      }

      // we need to pass in the configured/selected options here, as they won't be fully updated on this.props during this callback yet
      var configuredProductOptions = initializeSelections
        ? defaultSelectedOptions
        : this.props.selectedProductOptions;
      this.selectAndClearOptions(optionSetInfoList, configuredProductOptions);
    });
  };

  selectAndClearOptions = (visibleOptionSets, configuredProductOptions) => {
    const selectedProductOptions = _.cloneDeep(configuredProductOptions); // clone just to be safe
    for (var i = 0; i < visibleOptionSets.length; i++) {
      const optionSetInfo = visibleOptionSets[i];
      const selectedOption = selectedProductOptions.find(
        (x) => x.productOptionSetId === optionSetInfo.optionSet.id
      );
      const selectedOptionId =
        selectedOption.productOptionId &&
        selectedOption.productOptionId !== '' &&
        selectedOption.productOptionId !== null
          ? selectedOption.productOptionId
          : undefined;
      const availableOptionIds = [];
      for (
        var j = 0;
        j < optionSetInfo.optionSet.productOptionsBySeries.length;
        j++
      ) {
        const productOptionBySeries =
          optionSetInfo.optionSet.productOptionsBySeries[j];

        for (var k = 0; k < productOptionBySeries.productOptions.length; k++) {
          const productOption = productOptionBySeries.productOptions[k];

          if (productOption.available) {
            availableOptionIds.push(productOption.id);
          }

          if (
            !productOption.available &&
            selectedOptionId === productOption.id
          ) {
            // The option is not available AND is selected
            // --> unselect this option
            selectedOption.productOptionId = '';
          } else if (
            productOption.available &&
            optionSetInfo.optionSet.isSelectionRequired &&
            productOptionBySeries.defaultProductOptionId === productOption.id &&
            !selectedOptionId
          ) {
            // The option is available, the set requires a selection, this option is the default, and we have no selected option
            // --> select this option
            selectedOption.productOptionId = productOption.id;
          }
        }
      }

      const newSelectedOptionId =
        selectedOption.productOptionId &&
        selectedOption.productOptionId !== '' &&
        selectedOption.productOptionId !== null
          ? selectedOption.productOptionId
          : undefined;
      if (!newSelectedOptionId) {
        // we have not selected anything, so check to see if only one option is available
        if (
          availableOptionIds.length === 1 &&
          optionSetInfo.optionSet.isSelectionRequired
        ) {
          selectedOption.productOptionId = availableOptionIds[0];
        }
      }
    }

    const requiredOptionSets = visibleOptionSets.filter(
      (set) => set.optionSet.isSelectionRequired && set.hasAvailableOptions
    );
    const allOptionsConfigured =
      requiredOptionSets.length === 0 ||
      requiredOptionSets.every((set) => {
        const configuredQuoteProductOption = selectedProductOptions.find(
          (option) =>
            option.productOptionSetId === set.optionSet.id &&
            !!option.productOptionId
        );
        return configuredQuoteProductOption;
      });

    this.props.onProductChange(
      {
        allOptionsConfigured: allOptionsConfigured,
        productOptions: selectedProductOptions,
      },
      false
    );
  };

  updateProductOptionSelection = function (optionSetId, productOptionId) {
    const listWithoutItem = this.props.selectedProductOptions.filter(
      (x) => x.productOptionSetId !== optionSetId
    );
    const item = this.props.selectedProductOptions.find(
      (x) => x.productOptionSetId === optionSetId
    );

    this.props.onProductChange(
      {
        productOptions: [
          ...listWithoutItem,
          new QuoteProductOption({ ...item, productOptionId }),
        ],
      },
      true
    );
  };

  isProductOptionDependencyMet = (dependency) => {
    if (!dependency || dependency === null) {
      return true;
    }

    switch (dependency.groupLogic) {
      case 'All':
        return dependency.groups.every((x) =>
          this.isProductOptionDependencyGroupMet(x)
        );
      case 'Any':
        return dependency.groups.find((x) =>
          this.isProductOptionDependencyGroupMet(x)
        )
          ? true
          : false;
      default:
        throw Error(`${dependency.groupLogic} is not supported`);
    }
  };

  isProductOptionDependencyGroupMet = (group) => {
    switch (group.conditionLogic) {
      case 'All':
        return group.conditions.every((x) =>
          this.isProductOptionDependencyConditionMet(x)
        );
      case 'Any':
        return group.conditions.find((x) =>
          this.isProductOptionDependencyConditionMet(x)
        )
          ? true
          : false;
      default:
        throw Error(`${group.conditionLogic} is not supported`);
    }
  };

  isProductOptionDependencyConditionMet = (condition) => {
    switch (condition.type) {
      case 'ProductOption':
        const selectedOption = this.props.selectedProductOptions.find(
          (x) => x.productOptionId === condition.value
        );
        return selectedOption && selectedOption !== null;

      case 'Product':
        return this.props.selectedProduct.id === condition.value;

      case 'LuggingIsCouplerType':
      case 'LuggingIsCouplerStyle':
      case 'LuggingIsCouplerOilReady':
        if (
          this.props.couplerDecision !== couplerDecision.amiCoupler &&
          this.props.couplerDecision !== couplerDecision.existingCoupler &&
          this.props.couplerDecision !== couplerDecision.amiTiltRotator &&
          this.props.couplerDecision !== couplerDecision.existingTiltRotator
        ) {
          //console.info('LuggingIsCoupler(Type/Style) condition is not valid due to coupler decision');
          return false;
        }

        if (!this.props.couplerStyle) {
          //console.info('LuggingIsCoupler(Type/Style) condition is not valid due to coupler style missing');
          return false; // NOTE: this shouldn't happen, because if a coupler decision was made, we should always have a coupler style specified
        }

        if (condition.type === 'LuggingIsCouplerType') {
          return (
            !condition.value ||
            condition.value === null ||
            this.props.couplerStyle.type === condition.value
          );
        }
        if (condition.type === 'LuggingIsCouplerOilReady') {
          return (
            !condition.value ||
            condition.value === null ||
            this.props.couplerOilReady === condition.value
          );
        }

        return this.props.couplerStyle.id === condition.value;

      case 'LuggingIsDirectPinOn':
        var result = this.props.couplerDecision === couplerDecision.directPinOn;
        //console.info(`LuggingIsDirectPinOn condition is ${result ? 'valid' : 'not valid'}`);
        return result;

      default:
        throw Error(`${condition.type} is not supported`);
    }
  };

  getSelectedProductOptionIdFor = (optionSetId) => {
    const selectedProductOption = this.props.selectedProductOptions.find(
      (x) => x.productOptionSetId === optionSetId
    );
    return selectedProductOption ? selectedProductOption.productOptionId : null;
  };

  getDefaultSeriesIdFor = (optionSetInfo) => {
    const productOptionId = this.getSelectedProductOptionIdFor(
      optionSetInfo.optionSet.id
    );
    if (productOptionId) {
      const bySeriesGroup = optionSetInfo.optionSet.productOptionsBySeries.find(
        (x) => x.productOptions.some((y) => y.id === productOptionId)
      );
      return bySeriesGroup.seriesId;
    } else {
      const { brands } = this.props.productConfiguration.productOption;
      if (
        optionSetInfo.preferredSeriesId &&
        optionSetInfo.includesSeries(optionSetInfo.preferredSeriesId)
      ) {
        return optionSetInfo.preferredSeriesId;
      } else if (
        optionSetInfo.preferredBrandId &&
        optionSetInfo.includesBrand(optionSetInfo.preferredBrandId, brands)
      ) {
        const optionGroup = optionSetInfo.getFirstProductOptionGroupFromBrand(
          optionSetInfo.preferredBrandId,
          brands
        );
        return optionGroup.seriesId;
      } else if (
        optionSetInfo.optionSet.defaultSeriesId &&
        optionSetInfo.includesSeries(optionSetInfo.optionSet.defaultSeriesId)
      ) {
        return optionSetInfo.optionSet.defaultSeriesId;
      } else {
        return null;
      }
    }
  };

  getDefaultProductOptionIdFor = (optionSetInfo) => {
    const { selectedInventoryItem } = this.props;
    const inventoryOption = selectedInventoryItem
      ? selectedInventoryItem.productOptions.find(
          (x) => x.productOptionSetId === optionSetInfo.optionSet.id
        )
      : null;
    if (inventoryOption && inventoryOption.productOptionId) {
      return inventoryOption.productOptionId;
    }

    if (!optionSetInfo.optionSet.isSelectionRequired) {
      return null;
    }

    const defaultSeriesId = this.getDefaultSeriesIdFor(optionSetInfo);
    const series = optionSetInfo.getSeries(defaultSeriesId);
    if (!series) {
      return null;
    }

    if (
      optionSetInfo
        .getAvailableProductOptionIds()
        .find((x) => x === series.defaultProductOptionId)
    ) {
      return series.defaultProductOptionId;
    }

    return null;
  };

  scrollTo = (name) => {
    if (this._scroller) this._scroller.scrollTo(name);
  };

  onNotesChange = (notes) => {
    this.props.onProductChange({
      notes: notes,
    });
  };
  

  onPaintColorChange = (value) => {  
    this.props.onProductChange({
      'paintColor'  : value,
    });
  };

  onManufacturingInstructionsChange = (value) => {       
      const manufacturingInstructions = (value || '').trim() === '' ? '' : value;
      const requiresReview =  (manufacturingInstructions!=='');
      this.props.onProductChange({
        'manufacturingInstructions'  : manufacturingInstructions,
        'requiresReview': requiresReview,
         'isReviewed': !requiresReview
      });   
  };

  onRequestCustomizationsToggle = (value) => {          
    this.setState({ showInstructions : value})
    if(!value) 
       this.onManufacturingInstructionsChange('');
 
  };

  render() {
    const  paintColorList = {
      amiGrey: 'AMI Grey',
      black: 'Black',
      catYellow: 'CAT Yellow',
      casePowerTanYellow: 'Case PowerTan Yellow',
      deereIndustrialYellow: 'Deere Industrial Yellow',
      komatsuYellow: 'Komatsu Yellow',
      kubotaOrange: 'Kubota Orange',
      volvoGrey: 'Volvo Grey',
    };
    const {
      onLastOptionSelected,
      notes,
      paintColor,
      manufacturingInstructions,
      productGroup,
      originalProductOptions,
      isInventoryMode,
      selectedInventoryItem,
      selectedProduct,
      hideLeadTimeAndPrices,
    } = this.props;

    return (
      <Fragment>
        <ScrollView ref={(scroller) => (this._scroller = scroller)}>
          <Content
            selectedProduct={selectedProduct}
            productGroup={productGroup}
            show={this.state.optionSetInfoList.length > 0}
            scrollOnShow={true}
            optionSetInfoList={this.state.optionSetInfoList}
            updateProductOptionSelection={this.updateProductOptionSelection}
            getSelectedProductOptionIdFor={this.getSelectedProductOptionIdFor}
            getDefaultSeriesIdFor={this.getDefaultSeriesIdFor}
            scrollTo={this.scrollTo}
            onLastOptionSelected={onLastOptionSelected}
            originalProductOptions={originalProductOptions}
            isInventoryMode={isInventoryMode}
            selectedInventoryItem={selectedInventoryItem}
            hideLeadTimeAndPrices={hideLeadTimeAndPrices}
          />
            
        </ScrollView>
        { isAmiUserCanEditPaintColor() && (
         <FormGroup className={`option-set-form`}>
              <div className="row">
                <div className="col-9 col-sm-10">
                  <div className="optionset-name">Select Paint Scheme</div>      
                </div>
                <div className="col-3 col-sm-2 d-flex justify-content-end">
                      <FormToggle
                        checked={this.state.showPaintColor}
                        disabled ={isInventoryMode}
                        label=""
                        onChange={(checked) => this.setState({ showPaintColor : checked}) }
                      />                               
                </div>
              </div>
              { this.state.showPaintColor && (
                  <div className="mt-3">
                    <Dropdown           
                    items={Object.keys(paintColorList).map((key) => ({
                      id: paintColorList[key],
                      label: paintColorList[key],
                    }))}
                    selectedId={paintColor}
                    onChange={(e) => this.onPaintColorChange(e)}    
                  />               
                  </div>
              )
              }
      </FormGroup>)

        }     
        {!isInventoryMode && (
          <ProductNotes
            notes={notes}
            notesField={productGroup.notesField}
            onNotesChange={this.onNotesChange}
          />
        )}
        <div>  
          <div className="hr-divider">
            <h3 className="hr-divider-content hr-divider-heading">
            Request Customizations
            </h3>
          </div>
          <FormGroup className={`option-set-form`}>
            <div className="row">
                  <div className="col-9 col-sm-10">
                    <div className="optionset-name">Request Customizations</div>     
                  </div>
                  <div className="col-9 col-sm-10">
                  { !this.state.showInstructions && <p>Note: Adding manufacturing customizations will require a review of the quote before lead times & pricing can be confirmed.</p> }
                  { this.state.showInstructions &&                   
                    <div className="alert alert-warning py-1 mt-1">
                          <div className='d-flex align-items-center'>
                            <div className='flex-grow-0 me-2'>
                              <i className="fa fa-exclamation-circle text-danger" />
                            </div>
                            <div className='flex-grow-0'>
                            Lead Times & Pricing cannot be confirmed until Requested Customizations are approved.
                            </div>
                          </div>
                        </div>
                    }
                  </div>               
                  <div className="col-3 col-sm-2 d-flex justify-content-end">
                      <FormToggle
                        checked={this.state.showInstructions}
                        label=""
                        onChange={(checked) => this.onRequestCustomizationsToggle(checked) }
                      />                               
                  </div>
                </div>
                { this.state.showInstructions && (
                    <div className="mt-3">
                    <FormInput
                    id="manufacturingInstructions"
                    maxLength={500}
                    type="textarea"
                    rows={2}
                    value={manufacturingInstructions}
                    onChange={(e) => this.onManufacturingInstructionsChange(e.target.value)}                      
                  />
                    </div>
                )
                }
        </FormGroup>
       </div>
      
      </Fragment>
    );
  }
}
